import {SiteInfo, WixSdk} from '@wix/js-sdk-wrapper';
import {inject, injectable, decorate} from 'inversify';
import {combineLatest, from, merge, NEVER, Observable, of} from 'rxjs';
import {distinctUntilChanged, map, share} from 'rxjs/operators';
import {WidgetSettings} from '../../../../../typings/settings';
import {services} from '../../../../ioc/services';
import {artifactVersion} from '../../../../logging/artifact-version';
import {IGlobal} from '../../../../services/Global/IGlobal';
import {debug, debugScoped} from '../../../../utils/debug';
import {debugSettings} from '../../../../utils/debugPanel';
import {getWindowUrl} from '../../../../utils/env/get-get-window-url';
import {getLocale} from '../../../../utils/env/locale';
import {areObjectsEqual} from '../../../../utils/objects';
import {getSharedTrackId, getUrlPrefixes} from '../../../../utils/url';
import {getBiToken, getInstanceId} from '../../../../utils/wix-sdk';
import {getUserSessionToken} from '../../../../utils/wixSdk/getUserSessionToken';
import {WIX_MUSIC_TPA_ID} from '../../../constants';
import {getViewMode, isInSite, viewMode$, ViewModes} from '../../viewMode';
import {pageId$, rect$, styleParams$} from '../../wixSdkObservables';
import {ISettingsProvider} from './ISettingsProvider';

export class SettingsProvider implements ISettingsProvider {
  private readonly siteInfo$: Observable<SiteInfo>;
  private readonly userSessionToken$: Observable<string>;
  private readonly userId: string;
  private readonly instanceId: string;
  private readonly biToken: string;
  private readonly compId: string;
  private readonly locale: string;


  constructor(private readonly global: IGlobal) {
    this.siteInfo$ = from<Promise<SiteInfo>>(WixSdk.getSiteInfo());
    this.userSessionToken$ = from(getUserSessionToken());
    this.userId = WixSdk.Utils.getUid();
    this.instanceId = getInstanceId();
    this.biToken = getBiToken();
    this.compId = WixSdk.Utils.getCompId();
    this.locale = getLocale();
  }

  getSettings() {
    debugScoped('getSettings', 'getSettings');

    if (isInSite()) {
      if (debugSettings.skipFullSettings) {
        return this.getWarmupSettings();
      }
      return merge(this.getWarmupSettings(), this.getFullSettings());
    }
    return this.getFullSettings();
  }

  private getFullSettings(): Observable<WidgetSettings> {
    const {siteInfo$, userSessionToken$} = this;
    return combineLatest([styleParams$, siteInfo$, pageId$, userSessionToken$, rect$, viewMode$]).pipe(
      map(
        ([styleParams, siteInfo, pageId, userSessionToken, {scale = 1, rect}, viewMode]): WidgetSettings => {
          return {
            ...styleParams,
            ...this.getCommonSettings(viewMode, styleParams.booleans.autoplay),
            sharedTrackId: getSharedTrackId(siteInfo),
            siteUrl: siteInfo.url,
            baseUrl: siteInfo.baseUrl,
            pageId,
            sToken: userSessionToken,
            rect,
            scale,
          };
        },
      ),
      distinctUntilChanged((prev, curr) => areObjectsEqual(prev, curr)),
      debug('getSettings', 'new settings'),
      share(),
    );
  }

  private getWarmupSettings(): Observable<WidgetSettings> {
    const widgetMetaData = this.global.getWidgetData();
    const styleParams = widgetMetaData?.style;

    if (!styleParams) {
      return NEVER;
    }

    const viewMode = getViewMode();
    return of({
      ...styleParams,
      ...this.getCommonSettings(viewMode, styleParams.booleans.autoplay),
      rect: {
        width: 0,
        height: 0,
      },
      scale: 1,
    }).pipe(debug('getSettings', 'warmup settings'));
  }

  private getCommonSettings(viewMode: ViewModes, autoplay: boolean) {
    return {
      viewMode,
      artifactVersion,
      tpaApplicationId: WIX_MUSIC_TPA_ID,
      instanceId: this.instanceId,
      compId: this.compId,
      userId: this.userId,
      biToken: this.biToken,
      locale: this.locale,
      siteOwnerId: WixSdk.Utils.getSiteOwnerId(),
      isGetWindowEnabled: true,
      getWindowUrl: getWindowUrl(),
      isMobileView: WixSdk.Utils.getDeviceType() === 'mobile',
      urlPrefixes: getUrlPrefixes(),
      autoplay: autoplay && viewMode !== ViewModes.Editor,
    };
  }
}

decorate(injectable(), SettingsProvider);
decorate(inject(services.global) as ParameterDecorator, SettingsProvider, 0);
